import { ref, computed, inject, type Ref } from 'vue'
import { type FileRetrieve } from '@openapi'

export interface FileLoadingState {
  id: string
  name?: string
  isLoading: boolean
  isLoaded: boolean
  file?: FileRetrieve
  error?: string
}

export default function useLegacyCollectionFileLoader() {
  const fetchFileById =
    inject<(id: string, options?: { skip_error_toast?: boolean }) => Promise<FileRetrieve>>('fetchFileById')

  // Try to get canvas-level file cache, fallback to local cache if not available (for non-canvas contexts)
  const canvasFileCache = inject<Ref<Map<string, any>>>('canvasFileCache', ref(new Map()))

  if (!fetchFileById) {
    throw new Error('fetchFileById is not provided in the injection context')
  }

  // Store file loading states (this is component-specific, not shared)
  const fileStates = ref<Record<string, FileLoadingState>>({})

  // Use canvas-level cache for files, with fallback to local cache
  const filesById = computed(() => {
    const result: Record<string, FileRetrieve> = {}
    for (const [key, value] of canvasFileCache.value.entries()) {
      if (key.startsWith('collection-file:') && value) {
        result[key.replace('collection-file:', '')] = value
      }
    }
    return result
  })

  // Track loading queues (component-specific)
  const loadingPromises = ref<Map<string, Promise<FileRetrieve | null>>>(new Map())

  // Computed state for easy access
  const allFileStates = computed(() => fileStates.value)

  // Initialize file states for a list of file IDs
  const initializeFiles = (fileIds: string[], fileNames?: Record<string, string>) => {
    fileIds.forEach((id) => {
      if (!fileStates.value[id]) {
        fileStates.value[id] = {
          id,
          name: fileNames?.[id],
          isLoading: false,
          isLoaded: false,
        }
      }
    })
  }

  // Load a single file by ID
  const loadFile = async (fileId: string): Promise<FileRetrieve | null> => {
    const cacheKey = `collection-file:${fileId}`

    // Check if already loaded in canvas cache
    if (canvasFileCache.value.has(cacheKey)) {
      return canvasFileCache.value.get(cacheKey)
    }

    // Check if already loading
    if (loadingPromises.value.has(fileId)) {
      return loadingPromises.value.get(fileId)!
    }

    // Update state to loading
    if (fileStates.value[fileId]) {
      fileStates.value[fileId].isLoading = true
    }

    // Create loading promise
    const loadingPromise = (async () => {
      try {
        const file = await fetchFileById(fileId, { skip_error_toast: true })

        // Store the loaded file in canvas cache
        canvasFileCache.value.set(cacheKey, file)

        // Update state
        if (fileStates.value[fileId]) {
          fileStates.value[fileId] = {
            ...fileStates.value[fileId],
            isLoading: false,
            isLoaded: true,
            file,
            name: file.name,
          }
        }

        return file
      } catch (error) {
        console.warn(`[useLegacyCollectionFileLoader] Failed to load file ${fileId}:`, error)

        // Update state with error
        if (fileStates.value[fileId]) {
          fileStates.value[fileId] = {
            ...fileStates.value[fileId],
            isLoading: false,
            isLoaded: false,
            error: error instanceof Error ? error.message : 'Failed to load file',
          }
        }

        return null
      } finally {
        // Remove from loading promises
        loadingPromises.value.delete(fileId)
      }
    })()

    // Store the promise
    loadingPromises.value.set(fileId, loadingPromise)

    return loadingPromise
  }

  // Load all files at once
  const loadAllFiles = async (fileIds: string[]): Promise<void> => {
    const promises = fileIds.map((id) => loadFile(id))
    await Promise.all(promises)
  }

  // Get file by ID (returns cached file or loading state)
  const getFile = (fileId: string): FileRetrieve | undefined => {
    return filesById.value[fileId]
  }

  // Get file loading state
  const getFileState = (fileId: string): FileLoadingState | undefined => {
    return fileStates.value[fileId]
  }

  // Check if file is loaded
  const isFileLoaded = (fileId: string): boolean => {
    return !!filesById.value[fileId]
  }

  // Check if file is loading
  const isFileLoading = (fileId: string): boolean => {
    return fileStates.value[fileId]?.isLoading ?? false
  }

  // Check if file has failed to load
  const hasFileFailed = (fileId: string): boolean => {
    return !!fileStates.value[fileId]?.error
  }

  // Check if file resolution is complete (either loaded or failed)
  const isFileResolved = (fileId: string): boolean => {
    return isFileLoaded(fileId) || hasFileFailed(fileId)
  }

  // Get file name (from loaded file or initial name)
  const getFileName = (fileId: string): string | undefined => {
    const file = filesById.value[fileId]
    if (file) return file.name

    const state = fileStates.value[fileId]
    return state?.name
  }

  // Reset all state
  const reset = () => {
    fileStates.value = {}
    // Note: We don't clear the canvas-level cache here as it's managed at canvas level
    loadingPromises.value.clear()
  }

  // Get loading statistics
  const getLoadingStats = () => {
    const states = Object.values(fileStates.value)
    return {
      total: states.length,
      loaded: states.filter((s) => s.isLoaded).length,
      loading: states.filter((s) => s.isLoading).length,
      failed: states.filter((s) => s.error).length,
      pending: states.filter((s) => !s.isLoaded && !s.isLoading && !s.error).length,
    }
  }

  return {
    // State
    fileStates: allFileStates,
    filesById: filesById,

    // Methods
    initializeFiles,
    loadFile,
    loadAllFiles,
    getFile,
    getFileState,
    getFileName,
    isFileLoaded,
    isFileLoading,
    hasFileFailed,
    isFileResolved,
    reset,
    getLoadingStats,
  }
}
